import { styled } from '@linaria/react';
import type { ReactNode } from 'react';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

/**
 * Visual tone of the banner, mapped to a background/border/accent palette.
 */
type FloraBannerVariant = 'info' | 'success' | 'warning';

/**
 * Shape of the banner. `'inline'` is a wide horizontal bar (full-width page
 * chrome); `'card'` is a compact, roughly square card for narrow surfaces like
 * a sidebar — icon and copy stack vertically with a full-width call-to-action.
 */
type FloraBannerLayout = 'inline' | 'card';

interface FloraBannerProps {
  /** Title line — the primary message. */
  title: string;
  /** Optional supporting copy shown beneath the title. */
  description?: string;
  /** Optional Material icon name rendered as the leading accent. */
  icon?: string;
  /** Label for the call-to-action button; the button only renders when both this and `onAction` are set. */
  actionLabel?: string;
  /** Invoked when the call-to-action button is clicked. */
  onAction?: () => void;
  /** When provided, renders a dismiss (✕) button wired to this callback. */
  onDismiss?: () => void;
  /** Visual tone; defaults to `'info'`. */
  variant?: FloraBannerVariant;
  /** Layout shape; defaults to `'inline'`. Use `'card'` for narrow surfaces. */
  layout?: FloraBannerLayout;
  /** Optional footer content rendered beneath the body in `'card'` layout (e.g. a pager). */
  footer?: ReactNode;
}

const VARIANT_ICON_COLOR: Record<FloraBannerVariant, string> = {
  info: 'var(--secondary-base)',
  success: 'var(--primary-base)',
  warning: 'var(--yellow-darkest)',
};

/**
 * Reusable inline informational banner for Flora surfaces. Renders a leading
 * icon, a title/description block, an optional call-to-action, and an optional
 * dismiss button. Pairs with `useDismissibleBanner` for per-banner dismissal
 * memory — this component is purely presentational and holds no visibility state.
 *
 * @param {FloraBannerProps} props - Banner content and handlers
 * @returns {JSX.Element} The rendered banner
 */
export function FloraBanner({
  title,
  description,
  icon,
  actionLabel,
  onAction,
  onDismiss,
  variant = 'info',
  layout = 'inline',
  footer,
}: FloraBannerProps) {
  const isCard = layout === 'card';
  return (
    <BannerRoot data-variant={variant} data-layout={layout} role="status">
      {icon && (
        <IconWrap>
          <Icon name={icon} size={20} color={VARIANT_ICON_COLOR[variant]} />
        </IconWrap>
      )}
      <TextBlock>
        <Text size="medium" weight="medium" color="var(--ink-darkest)">
          {title}
        </Text>
        {description && (
          <Text size="small" color="var(--ink-light)">
            {description}
          </Text>
        )}
      </TextBlock>
      {actionLabel && onAction && (
        <ActionWrap>
          <Button size="sm" variant="secondary" fullWidth={isCard} onClick={onAction}>
            {actionLabel}
          </Button>
        </ActionWrap>
      )}
      {onDismiss && (
        <DismissButton type="button" onClick={onDismiss} aria-label="Dismiss" data-dismiss>
          <Icon name="close" size={18} color="var(--ink-light)" />
        </DismissButton>
      )}
      {isCard && footer && <FooterSlot>{footer}</FooterSlot>}
    </BannerRoot>
  );
}

const BannerRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  background: var(--sky-lightest);
  box-sizing: border-box;

  &[data-variant='success'] {
    border-color: var(--primary-light);
    background: var(--primary-lightest);
  }

  &[data-variant='warning'] {
    border-color: var(--yellow-light);
    background: var(--yellow-lightest);
  }

  /* Card: compact, roughly square stack for narrow surfaces (e.g. sidebar).
     A shadow lifts it off the surface so it reads as a distinct card. */
  &[data-layout='card'] {
    position: relative;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
    border-radius: 16px;
    box-shadow: 0 4px 12px var(--flora-shadow-card);
  }

  /* On a tinted sidebar the default info fill matches the surface and vanishes;
     give the info card a white surface so it stands out. Tinted variants keep
     their own fill and rely on the shadow above for separation. */
  &[data-layout='card'][data-variant='info'] {
    background: var(--base-white);
    border-color: var(--sky-light);
  }

  /* Dismiss floats to the corner so it never crowds the stacked copy. */
  &[data-layout='card'] [data-dismiss] {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ActionWrap = styled.div`
  flex-shrink: 0;

  /* In card layout the CTA spans the full width beneath the copy. */
  [data-layout='card'] & {
    width: 100%;
    margin-top: 2px;
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-hover-subtle);
  }
`;

const FooterSlot = styled.div`
  width: 100%;
  margin-top: 2px;
  padding-top: 10px;
  border-top: 1px solid var(--sky-light);
`;

export type { FloraBannerProps, FloraBannerVariant };
